/**
 * Which events screen is open: the list, one event, or the editor (new or
 * an edit). One at a time, in one dialog layer (events/Host.tsx).
 */
import {
  GuildScheduledEventEntityType as ET,
  GuildScheduledEventStatus as ST,
  type GuildScheduledEvent,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import type { IconName } from '../ui/kit';
import type { Formatters, Translator } from '../i18n/core';
import type { DataState } from '../store/reducer';

export type EventsView =
  | { kind: 'list' }
  | { kind: 'event'; id: Snowflake }
  | {
      kind: 'edit';
      /** null: a new event. */
      id: Snowflake | null;
      /** A new event's channel, when it was made from one. */
      channelId?: Snowflake;
      /** Where Cancel goes back to. */
      back: 'list' | 'event' | null;
    };

export const useEventsUi = create<{ view: EventsView | null }>(() => ({ view: null }));

export function openEvents(): void {
  useEventsUi.setState({ view: { kind: 'list' } });
}
export function openEvent(id: Snowflake): void {
  useEventsUi.setState({ view: { kind: 'event', id } });
}
export function openEditor(
  id: Snowflake | null,
  o: { channelId?: Snowflake; back?: 'list' | 'event' | null } = {},
): void {
  useEventsUi.setState({
    view: { kind: 'edit', id, channelId: o.channelId, back: o.back ?? null },
  });
}
export function closeEvents(): void {
  useEventsUi.setState({ view: null });
}

/* ------------------------------------------------------- what it shows */

const DAY_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const DAY_YEAR_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** A moment in the viewer's own time: "Today at 20:00", "Tomorrow at 09:30", "Fri, Oct 31 at 20:00". */
export function whenAt(tr: Translator, iso: string, now = new Date()): string {
  const fmt: Formatters = tr.fmt;
  const d = new Date(iso);
  const time = fmt.time(d);
  const diff = fmt.dayDiff(d, now);
  if (diff === 0) return tr.t('ev.when.today', { time });
  if (diff === 1) return tr.t('ev.when.tomorrow', { time });
  const date = (d.getFullYear() === now.getFullYear() ? DAY_FMT : DAY_YEAR_FMT).format(d);
  return tr.t('ev.when.other', { date, time });
}

/** An event's time line: its start, its end when it has one, or how long ago it started. */
export function whenText(tr: Translator, e: GuildScheduledEvent, now = new Date()): string {
  if (e.status === ST.ACTIVE) {
    const at = e.nx_started_at ?? e.scheduled_start_time;
    return tr.t('ev.when.started', { ago: tr.fmt.rel(at, now) });
  }
  const start = whenAt(tr, e.scheduled_start_time, now);
  if (Date.parse(e.scheduled_start_time) < now.getTime())
    return tr.t('ev.when.late', { ago: tr.fmt.rel(e.scheduled_start_time, now) });
  if (!e.scheduled_end_time) return start;
  const end = new Date(e.scheduled_end_time);
  const sameDay = tr.fmt.dayDiff(end, new Date(e.scheduled_start_time)) === 0;
  return tr.t('ev.when.range', {
    start,
    end: sameDay ? tr.fmt.time(end) : whenAt(tr, e.scheduled_end_time, now),
  });
}

/** Where an event is: its channel's name, or its place. */
export function whereOf(
  d: DataState,
  tr: Translator,
  e: Pick<GuildScheduledEvent, 'entity_type' | 'channel_id' | 'entity_metadata'>,
): { icon: IconName; label: string } {
  if (e.entity_type === ET.EXTERNAL)
    return { icon: 'globe', label: e.entity_metadata?.location ?? '' };
  const c = e.channel_id ? d.channels[e.channel_id] : undefined;
  return {
    icon: e.entity_type === ET.STAGE_INSTANCE ? 'stage' : 'volume',
    label: c?.name ?? tr.t('ev.where.gone'),
  };
}

/** The viewer's time zone's name, for the editor's note. */
export function zoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** `yyyy-mm-dd` and `hh:mm` of a moment in the viewer's own time (the editor's fields). */
export function localParts(d: Date): { date: string; time: string } {
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

/** The moment the editor's local date and time fields name, or null when incomplete. */
export function fromLocalParts(date: string, time: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [y, mo, da] = date.split('-').map(Number) as [number, number, number];
  const [h, mi] = time.split(':').map(Number) as [number, number];
  const d = new Date(y, mo - 1, da, h, mi, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A new event's default start: the next full hour, at least 30 minutes away. */
export function defaultStart(now = new Date()): Date {
  const d = new Date(now.getTime() + 30 * 60_000);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}
